import type BaseEnumType from "@/app/services/baseEnumType";
import type { Order } from "@/app/services/generated/orderService";
import type { Visit } from "@/app/services/generated/visitService";
import Key from "@/app/types/common/key";
import type { Maybe } from "@/components/utils/typeHelpers";
import { translate } from "@/plugins/i18n";

export enum JourneyPanelTypeEnum {
  allOrders = 0,
  collection = 1,
  courier = 2,
}

export interface JourneyPanelTab {
  journeyPanelType: JourneyPanelTypeEnum;
  label: string;
  numberOfOrders?: number;
}

export interface OrderVisits {
  orders: Order[];
  visits: Visit[];
}

export class EnumDistributionItemAction implements BaseEnumType {
  private static allValues: EnumDistributionItemAction[] = []; // make sure this is top-most

  public static completeOrder = new EnumDistributionItemAction(
    1,
    "CompleteOrder",
    "enum_distribution_item_actions_mark_as_complete"
  );
  public static cancelOrder = new EnumDistributionItemAction(
    2,
    "CancelOrder",
    "enum_distribution_item_actions_cancel_order"
  );
  public static uncompleteOrder = new EnumDistributionItemAction(
    3,
    "UncompleteOrder",
    "enum_distribution_item_actions_uncomplete_order"
  );
  public static rescheduleOrder = new EnumDistributionItemAction(
    4,
    "RescheduleOrder",
    "enum_distribution_item_actions_reschedule_order"
  );
  public static completeVisit = new EnumDistributionItemAction(
    5,
    "CompleteVisit",
    "enum_distribution_item_actions_complete_visit"
  );
  public static cancelVisit = new EnumDistributionItemAction(
    6,
    "CancelVisit",
    "enum_distribution_item_actions_cancel_visit"
  );
  public static uncompleteVisit = new EnumDistributionItemAction(
    7,
    "UncompleteVisit",
    "enum_distribution_item_actions_uncomplete_visit"
  );
  public static rescheduleVisit = new EnumDistributionItemAction(
    8,
    "RescheduleVisit",
    "enum_distribution_item_actions_reschedule_visit"
  );
  public static viewDelivery = new EnumDistributionItemAction(
    9,
    "ViewDelivery",
    "enum_distribution_item_actions_view_delivery",
    true
  );
  public static viewVisit = new EnumDistributionItemAction(
    10,
    "ViewVisit",
    "enum_distribution_item_actions_view_visit",
    true
  );
  public static viewOrder = new EnumDistributionItemAction(
    11,
    "ViewOrder",
    "enum_distribution_item_actions_view_order",
    true
  );
  public static changeStatus = new EnumDistributionItemAction(
    12,
    "ChangeStatus",
    "enum_distribution_item_actions_change_status"
  );
  public static resolveAndCompleteOrder = new EnumDistributionItemAction(
    14,
    "ResolveAndCompleteOrder",
    "enum_distribution_item_actions_resolve_and_mark_as_complete"
  );
  public static ignoreAndCompleteOrder = new EnumDistributionItemAction(
    15,
    "ResolveAndCompleteOrder",
    "enum_distribution_item_actions_ignore_and_mark_as_complete"
  );
  public static editVisit = new EnumDistributionItemAction(
    16,
    "EditVisit",
    "enum_distribution_item_actions_edit_visit",
    false
  );
  public static printDocuments = new EnumDistributionItemAction(
    17,
    "PrintDocuments",
    "enum_distribution_item_actions_print_documents",
    false
  );

  public key: number;
  public value: string;
  public translationKey: string;
  public isRoutingAction?: boolean;

  private constructor(
    key: number,
    value: string,
    translationKey: string,
    isRoutingAction?: boolean
  ) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    this.isRoutingAction = isRoutingAction;
    EnumDistributionItemAction.allValues.push(this);
  }

  public get translation() {
    return translate(Key.fromString(this.translationKey));
  }

  public equals(other: Maybe<EnumDistributionItemAction>): boolean {
    return this.key === other?.key;
  }

  public isCompleteOrder() {
    return this.equals(EnumDistributionItemAction.completeOrder);
  }
  public isCancelOrder() {
    return this.equals(EnumDistributionItemAction.cancelOrder);
  }
  public isUncompleteOrder() {
    return this.equals(EnumDistributionItemAction.uncompleteOrder);
  }
  public isRescheduleOrder() {
    return this.equals(EnumDistributionItemAction.rescheduleOrder);
  }
  public isCompleteVisit() {
    return this.equals(EnumDistributionItemAction.completeVisit);
  }
  public isCancelVisit() {
    return this.equals(EnumDistributionItemAction.cancelVisit);
  }
  public isUncompleteVisit() {
    return this.equals(EnumDistributionItemAction.uncompleteVisit);
  }
  public isRescheduleVisit() {
    return this.equals(EnumDistributionItemAction.rescheduleVisit);
  }
  public isViewDelivery() {
    return this.equals(EnumDistributionItemAction.viewDelivery);
  }
  public isViewVisit() {
    return this.equals(EnumDistributionItemAction.viewVisit);
  }
  public isViewOrder() {
    return this.equals(EnumDistributionItemAction.viewOrder);
  }
  public isChangeStatus() {
    return this.equals(EnumDistributionItemAction.changeStatus);
  }
  public isResolveAndCompleteOrder() {
    return this.equals(EnumDistributionItemAction.resolveAndCompleteOrder);
  }
  public isIgnoreAndCompleteOrder() {
    return this.equals(EnumDistributionItemAction.ignoreAndCompleteOrder);
  }
  public isEditVisit() {
    return this.equals(EnumDistributionItemAction.editVisit);
  }
  public isPrintDocuments() {
    return this.equals(EnumDistributionItemAction.printDocuments);
  }

  public static getValues(): EnumDistributionItemAction[] {
    return EnumDistributionItemAction.allValues;
  }

  public static getByKey(key: number): EnumDistributionItemAction {
    const found = EnumDistributionItemAction.allValues.find(
      (e) => e.key === key
    );
    if (found === undefined) {
      throw new Error(`EnumDistributionItemAction - Key not found: ${key}`);
    }

    return found;
  }
}

export class EnumDistributionItemType implements BaseEnumType {
  private static allValues: EnumDistributionItemType[] = []; // make sure this is top-most

  public static order = new EnumDistributionItemType(
    1,
    "Order",
    "enum_distribution_item_type_order"
  );
  public static visit = new EnumDistributionItemType(
    2,
    "Visit",
    "enum_distribution_item_type_visit"
  );
  public static pickup = new EnumDistributionItemType(
    3,
    "Pickup",
    "distribution_ullage"
  );

  public key: number;
  public value: string;
  public translationKey?: string;

  private constructor(key: number, value: string, translationKey?: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumDistributionItemType.allValues.push(this);
  }

  public equals(other: Maybe<EnumDistributionItemType>): boolean {
    return this.key === other?.key;
  }

  public isOrder() {
    return this.equals(EnumDistributionItemType.order);
  }
  public isVisit() {
    return this.equals(EnumDistributionItemType.visit);
  }
  public isPickup() {
    return this.equals(EnumDistributionItemType.pickup);
  }

  public static getValues(): EnumDistributionItemType[] {
    return EnumDistributionItemType.allValues;
  }

  public static getByKey(key: number): EnumDistributionItemType {
    const found = EnumDistributionItemType.allValues.find((e) => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumDistributionItemType - Key not found: ${key}`);
    }

    return found;
  }
}
